import React from 'react';
import { Droplets, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';

export const WaterTracker = ({ currentOunces, goalOunces, onUpdate }) => {
  const percentage = Math.min((currentOunces / goalOunces) * 100, 100);
  const glassSize = 8; // 8oz per glass
  
  const addWater = (amount) => {
    onUpdate(Math.max(0, currentOunces + amount));
  };

  return (
    <div className="glass-card p-6" data-testid="water-tracker">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/20 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Water Intake</h3>
            <p className="text-xs text-white/50">Goal: {goalOunces} oz (1 gallon)</p>
          </div>
        </div>
        <span className="font-mono text-2xl text-cyan-400 neon-text">
          {currentOunces}<span className="text-sm text-white/50">oz</span>
        </span>
      </div>

      {/* Progress Bar */}
      <div className="relative h-4 bg-white/10 rounded-full overflow-hidden mb-4">
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-500"
          style={{ 
            width: `${percentage}%`,
            boxShadow: '0 0 10px rgba(0, 240, 255, 0.5)'
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-mono text-white/80">{Math.round(percentage)}%</span>
        </div>
      </div>

      {/* Quick Add Buttons */}
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addWater(-glassSize)}
          className="flex-1 h-10 rounded-xl bg-white/5 hover:bg-white/10 text-white"
          data-testid="water-minus-btn"
        >
          <Minus className="w-4 h-4 mr-1" />
          8oz
        </Button>
        
        <div className="flex gap-1">
          {[8, 16, 32].map((oz) => (
            <Button
              key={oz}
              variant="ghost"
              size="sm"
              onClick={() => addWater(oz)}
              className="h-10 px-3 rounded-xl bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400 font-mono text-sm"
              data-testid={`water-add-${oz}-btn`}
            >
              +{oz}
            </Button>
          ))}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addWater(glassSize)}
          className="flex-1 h-10 rounded-xl bg-cyan-400/20 hover:bg-cyan-400/30 text-cyan-400"
          data-testid="water-plus-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Glass
        </Button>
      </div>

      {/* Glass visualization */}
      <div className="flex justify-center gap-1 mt-4">
        {Array.from({ length: Math.ceil(goalOunces / glassSize) }).map((_, i) => (
          <div
            key={i}
            className={`w-2 h-6 rounded-full transition-all duration-300 ${
              (i + 1) * glassSize <= currentOunces
                ? 'bg-cyan-400'
                : 'bg-white/10'
            }`}
            style={{
              boxShadow: (i + 1) * glassSize <= currentOunces 
                ? '0 0 4px rgba(0, 240, 255, 0.5)' 
                : 'none'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default WaterTracker;
